// Weekly login rewards (replaced the daily coin claim). A coin-bag button in
// the menu's top bar (and in the game's top-right icon row), with a dot while today's reward is unclaimed, opens a
// pop-out panel: the 7 days of the week (Monday first) and their rewards,
// claimed days ticked, today highlighted with a Claim button, later days
// locked. Days follow the player's own calendar (they turn at local midnight) and a
// missed day cannot be claimed later. Free orb pulls can be opened right here
// with the orb window, or from the shop.
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_EQUIPPED,
  ORB_NAMES,
  ORB_TIERS,
  WEEKDAY_NAMES,
  WEEKLY_REWARDS,
  wizardLook,
  type Me,
  type OrbResult,
  type OrbTier,
  type WeeklyReward,
} from "@wp/shared";
import { formatCoins, formatDuration } from "../../lib/format";
import { useNow } from "../../lib/useNow";
import { api } from "../../net/api";
import { useSettings } from "../../settings/SettingsContext";
import { Icon } from "../common/Icon";
import { Tip } from "../common/Tip";

const OrbOpeningWindow = lazy(() => import("../common/OrbOpening").then((m) => ({ default: m.OrbOpeningWindow })));

/** How often the button checks whether a new day has started. */
const DAY_CHECK_MS = 30_000;

export function rewardLabel(reward: WeeklyReward): string {
  return reward.kind === "coins" ? `${formatCoins(reward.coins)} coins` : `Free ${ORB_NAMES[reward.tier]} pull`;
}

/** True when today's reward (the player's day) is still waiting, or a new day started since `me` was read. */
export function rewardWaiting(me: Me, serverNowMs: number): boolean {
  return serverNowMs >= me.weekly.nextDayAt || !me.weekly.claimed[me.weekly.today];
}

interface Props {
  me: Me;
  onMeChange: (me: Me) => void;
  /** Re-read the account (a new day started). */
  onRefresh: () => void;
  /** In a game: the button sits in the top-right icon row and the panel hangs from the screen's top right. */
  inGame?: boolean;
}

export function WeeklyRewardsButton({ me, onMeChange, onRefresh, inGame = false }: Props) {
  const [open, setOpen] = useState(false);
  const now = useNow(true, open ? 1000 : DAY_CHECK_MS);
  const serverNow = now + api.serverOffset();
  const stale = serverNow >= me.weekly.nextDayAt;
  const waiting = rewardWaiting(me, serverNow);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const [pull, setPull] = useState<OrbResult | null>(null);
  const [pulling, setPulling] = useState(false);
  const [orbError, setOrbError] = useState<string | null>(null);
  const { settings } = useSettings();
  const calm = settings.accessibility.photosensitive;
  const animate = !settings.game.reduceAnimations && !calm;
  const look = useMemo(() => wizardLook({ ...DEFAULT_EQUIPPED, ...me.equipped }), [me.equipped]);

  // A new day started while the menu was open: re-read the account (once per day).
  const refreshedFor = useRef(0);
  useEffect(() => {
    if (!stale || refreshedFor.current === me.weekly.nextDayAt) return;
    refreshedFor.current = me.weekly.nextDayAt;
    onRefresh();
  }, [stale, me.weekly.nextDayAt, onRefresh]);

  // Close the pop-out on Escape or a click outside it (not while an orb is opening).
  useEffect(() => {
    if (!open || pull) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, pull]);

  async function openFree(tier: OrbTier) {
    setPulling(true);
    setOrbError(null);
    const res = await api.openOrb(tier, true);
    setPulling(false);
    if (!res.ok) return setOrbError(res.error.message);
    setPull(res.value);
    onMeChange(res.value.me);
  }

  return (
    <span className={`rewards-wrap ${inGame ? "in-game" : ""}`} ref={wrapRef}>
      <Tip text="Weekly login rewards: claim a gift every day." side={inGame ? "left" : "bottom"}>
        <button
          type="button"
          className={`icon-square rewards-button ${waiting ? "has-reward" : ""}`}
          onClick={() => setOpen((o) => !o)}
          aria-label={waiting ? "Login rewards (today's reward is waiting)" : "Login rewards"}
          aria-expanded={open}
          aria-haspopup="dialog"
          data-testid="rewards-button"
        >
          <Icon name="moneyBag" size={inGame ? 30 : undefined} />
          {waiting && <span className="reward-dot" aria-hidden="true" data-testid="rewards-dot" />}
        </button>
      </Tip>
      {open && (
        <RewardsPanel
          me={me}
          serverNow={serverNow}
          stale={stale}
          busy={pulling}
          orbError={orbError}
          onMeChange={onMeChange}
          onOpenOrb={(tier) => void openFree(tier)}
          onClose={() => setOpen(false)}
        />
      )}
      {pull && (
        <div className="reward-orb-layer">
          <Suspense fallback={null}>
            <OrbOpeningWindow
              key={`${pull.itemId}:${pull.me.coins}:${pull.me.freeOrbs[pull.tier]}`}
              pull={pull}
              look={look}
              calm={calm}
              quick={!animate}
              canAgain={!pulling && me.freeOrbs[pull.tier] > 0}
              onAgain={() => void openFree(pull.tier)}
              onClose={() => setPull(null)}
            />
          </Suspense>
        </div>
      )}
    </span>
  );
}

function RewardsPanel({
  me,
  serverNow,
  stale,
  busy,
  orbError,
  onMeChange,
  onOpenOrb,
  onClose,
}: {
  me: Me;
  serverNow: number;
  stale: boolean;
  busy: boolean;
  orbError: string | null;
  onMeChange: (me: Me) => void;
  onOpenOrb: (tier: OrbTier) => void;
  onClose: () => void;
}) {
  const [claiming, setClaiming] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { today, claimed, nextDayAt } = me.weekly;
  const claimedToday = claimed[today] === true && !stale;
  const freeTiers = ORB_TIERS.filter((t) => me.freeOrbs[t] > 0);

  async function claim() {
    setClaiming(true);
    setError(null);
    setMessage(null);
    const res = await api.claimWeekly();
    setClaiming(false);
    if (!res.ok) {
      setError(res.error.message);
      const fresh = await api.me();
      if (fresh.ok) onMeChange(fresh.value);
      return;
    }
    onMeChange(res.value.me);
    const r = res.value.reward;
    setMessage(r.kind === "coins" ? `+${formatCoins(r.coins)} coins!` : `You got a free ${ORB_NAMES[r.tier]} pull! Open it below or in the shop.`);
  }

  return (
    <section className="rewards-panel window" role="dialog" aria-label="Weekly login rewards" data-testid="rewards-panel">
      <header className="titlebar">
        <span className="led" aria-hidden="true" />
        <strong>Weekly rewards</strong>
        <button type="button" className="tb-btn close" onClick={onClose} aria-label="Close">
          <Icon name="close" size={14} />
        </button>
      </header>
      <div className="rewards-body">
        <p className="small muted rewards-note">
          A new gift every day. Days reset at midnight, and a missed day can&apos;t be claimed later.
        </p>
        <ol className="reward-days">
          {WEEKLY_REWARDS.map((reward, i) => {
            const isToday = i === today && !stale;
            const done = claimed[i] === true && !stale;
            const state = done ? "claimed" : isToday ? "today" : i < today ? "missed" : "locked";
            return (
              <li
                key={i}
                className={`reward-day ${state} ${reward.kind === "orb" ? `orb-tint ${reward.tier}` : "coins"}`}
                data-testid={`reward-day-${i}`}
                data-state={state}
              >
                <span className="reward-weekday">{isToday ? "Today" : WEEKDAY_NAMES[i]!.slice(0, 3)}</span>
                <span className="reward-pic" aria-hidden="true">
                  {reward.kind === "orb" ? <span className="orb-art" /> : <Icon name="coin" size={30} />}
                </span>
                <span className="reward-what">
                  {reward.kind === "coins" ? `${formatCoins(reward.coins)} coins` : ORB_NAMES[reward.tier]}
                </span>
                <span className="reward-state">
                  {state === "claimed" ? (
                    <>
                      <span aria-hidden="true">✓</span> Claimed
                    </>
                  ) : state === "today" ? (
                    "Ready!"
                  ) : state === "missed" ? (
                    "Missed"
                  ) : (
                    <>
                      <Icon name="lock" size={11} /> Locked
                    </>
                  )}
                </span>
                <span className="sr-only">{`${WEEKDAY_NAMES[i]}: ${rewardLabel(reward)}, ${state}`}</span>
              </li>
            );
          })}
        </ol>
        <div className="rewards-claim">
          {claimedToday ? (
            <button type="button" className="btn" disabled data-testid="rewards-claim">
              Claimed · next in {formatDuration(nextDayAt - serverNow)}
            </button>
          ) : (
            <button
              type="button"
              className="btn gold big"
              disabled={claiming || stale}
              onClick={() => void claim()}
              data-testid="rewards-claim"
            >
              <Icon name="gift" size={18} /> Claim {stale ? "" : rewardLabel(WEEKLY_REWARDS[today]!)}
            </button>
          )}
          {message && (
            <p className="rewards-msg" role="status" data-testid="rewards-msg">
              {message}
            </p>
          )}
          {(error ?? orbError) && (
            <p className="form-error" role="alert">
              {error ?? orbError}
            </p>
          )}
        </div>
        {freeTiers.length > 0 && (
          <div className="free-pulls" data-testid="free-pulls">
            <h3>Free orb pulls</h3>
            <ul>
              {freeTiers.map((tier) => (
                <li key={tier} className={`orb-tint ${tier}`}>
                  <span className="orb-art" aria-hidden="true" />
                  <span>
                    {ORB_NAMES[tier]} <span className="muted">×{me.freeOrbs[tier]}</span>
                  </span>
                  <button
                    type="button"
                    className="btn small primary"
                    disabled={busy}
                    onClick={() => onOpenOrb(tier)}
                    data-testid={`rewards-open-${tier}`}
                  >
                    Open
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
